import { Link } from "wouter";

// Public support page. Linked as the App Store "Support URL", which Apple
// requires (guideline 1.5) to lead somewhere with real contact information.
// Must stay reachable signed out, since people arrive here from the App Store
// listing before they have an account.
const SUPPORT_EMAIL = "hello@trystoop.com";

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <section className="space-y-2">
      <h2 className="text-lg font-semibold">{title}</h2>
      <div className="space-y-2 text-sm text-muted-foreground">{children}</div>
    </section>
  );
}

export default function Support() {
  return (
    <div className="min-h-screen bg-background">
      <main className="mx-auto w-full max-w-2xl space-y-8 px-4 py-10">
        <header className="space-y-2">
          <h1 className="text-2xl font-bold tracking-tight">Stoop Support</h1>
          <p className="text-sm text-muted-foreground">
            Stoop is a neighborhood food marketplace for buying directly from local makers
            and growers. If something isn't working or you have a question, email us and a
            person will read it.
          </p>
        </header>

        <div className="rounded-lg border bg-muted/30 p-4">
          <p className="text-sm font-semibold text-foreground">Contact us</p>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="mt-1 block text-lg font-semibold text-primary underline underline-offset-2"
          >
            {SUPPORT_EMAIL}
          </a>
          <p className="mt-2 text-xs text-muted-foreground">
            We aim to reply within one business day. If it's about a specific order, include
            the order number so we can look it up.
          </p>
        </div>

        <Section title="Common questions">
          <p>
            <span className="font-medium text-foreground">Where's my order?</span> Open Orders
            in the app to see its current status and message the maker directly. If a
            community driver is delivering it, the scheduled pickup window is shown there too.
          </p>
          <p>
            <span className="font-medium text-foreground">I need a refund.</span> Request a
            cancellation from the order screen and the maker will be notified. For an order
            that's already been picked up or delivered, message the maker first, then email us
            if you can't reach a resolution.
          </p>
          <p>
            <span className="font-medium text-foreground">How do I sell on Stoop?</span> Create
            an account, open the account menu, and switch to Seller mode. You'll connect a
            Stripe account so payouts go directly to your bank.
          </p>
          <p>
            <span className="font-medium text-foreground">How do I delete my account?</span> In
            the app, go to Account, then Security, then Delete account. This removes your
            personal information and deactivates your listings.
          </p>
          <p>
            <span className="font-medium text-foreground">Is my food safe?</span> Stoop is a
            marketplace, not a producer. Makers are responsible for their own food safety and
            licensing. Ask a maker about ingredients, allergens, and preparation before you buy,
            especially if you have an allergy.
          </p>
        </Section>

        <Section title="Report a problem">
          <p>
            To report a listing, review, message, or person, use the Report option on that item
            in the app. Reports go to a moderation queue we review daily. You can also block
            someone from the chat menu, which stops all messages between you.
          </p>
          <p>
            For anything urgent or safety-related, email {SUPPORT_EMAIL} directly.
          </p>
        </Section>

        <Section title="More">
          <p className="flex flex-wrap gap-x-4 gap-y-1">
            <Link href="/privacy" className="text-primary underline underline-offset-2">
              Privacy Policy
            </Link>
            <Link href="/terms-of-use" className="text-primary underline underline-offset-2">
              Terms of Use
            </Link>
            <Link href="/" className="text-primary underline underline-offset-2">
              Open Stoop
            </Link>
          </p>
        </Section>

        <footer className="border-t pt-6 text-xs text-muted-foreground">
          MarketLink Labs LLC · {SUPPORT_EMAIL}
        </footer>
      </main>
    </div>
  );
}
